'use client'

import React, { useEffect, useState } from 'react'
import { motion, useMotionValue, useSpring } from 'framer-motion'
import { useHoverCapable } from '@/hooks/useHoverCapable'

const cursorColors = [
  { hex: '#a3e635', rgba: '163, 230, 53' },   // Neon Green
  { hex: '#38bdf8', rgba: '56, 189, 248' },   // Cyber Cyan Blue
  { hex: '#f43f5e', rgba: '244, 63, 94' },    // Electric Rose Red
  { hex: '#a855f7', rgba: '168, 85, 247' },   // Holographic Purple
  { hex: '#fbbf24', rgba: '251, 191, 36' },   // Solar Gold
  { hex: '#ec4899', rgba: '236, 72, 153' },   // Neon Pink
  { hex: '#2dd4bf', rgba: '45, 212, 191' },   // Emerald Teal
]

interface Ripple {
  id: number
  x: number
  y: number
  color: string
}

export default function CustomCursor() {
  const canHover = useHoverCapable()
  const [isHovered, setIsHovered] = useState(false)
  const [isVisible, setIsVisible] = useState(false)
  const [colorIndex, setColorIndex] = useState(0)
  const [clickRipples, setClickRipples] = useState<Ripple[]>([])

  // Raw mouse coordinates
  const mouseX = useMotionValue(-100)
  const mouseY = useMotionValue(-100)

  // Outer ring spring physics (slightly softer damping for liquid lag)
  const ringX = useSpring(mouseX, { stiffness: 450, damping: 28, mass: 0.5 })
  const ringY = useSpring(mouseY, { stiffness: 450, damping: 28, mass: 0.5 })

  // Inner dot spring physics (faster tracking)
  const dotX = useSpring(mouseX, { stiffness: 850, damping: 35, mass: 0.2 })
  const dotY = useSpring(mouseY, { stiffness: 850, damping: 35, mass: 0.2 })

  useEffect(() => {
    if (!canHover) return

    const handleMouseMove = (e: MouseEvent) => {
      mouseX.set(e.clientX)
      mouseY.set(e.clientY)
      if (!isVisible) setIsVisible(true)
    }

    const handleMouseOver = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      const isInteractive = target.closest('button, a, input, [role="button"], .cursor-pointer, .glass-panel')
      setIsHovered(!!isInteractive)
    }

    const handleMouseLeave = () => {
      setIsVisible(false)
    }

    const handleClick = (e: MouseEvent) => {
      // Smoothly cycle to the next vibrant studio color
      const nextIndex = (colorIndex + 1) % cursorColors.length
      setColorIndex(nextIndex)

      // Trigger a fading shockwave ripple at the click coordinates
      const newRipple: Ripple = {
        id: Date.now() + Math.random(),
        x: e.clientX,
        y: e.clientY,
        color: cursorColors[nextIndex].rgba,
      }
      setClickRipples((prev) => [...prev, newRipple])
      setTimeout(() => {
        setClickRipples((prev) => prev.filter((r) => r.id !== newRipple.id))
      }, 700)
    }

    window.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseover', handleMouseOver)
    document.addEventListener('mouseleave', handleMouseLeave)
    window.addEventListener('click', handleClick)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseover', handleMouseOver)
      document.removeEventListener('mouseleave', handleMouseLeave)
      window.removeEventListener('click', handleClick)
    }
  }, [canHover, mouseX, mouseY, isVisible, colorIndex])

  if (!canHover || !isVisible) return null

  const activeColor = cursorColors[colorIndex]

  return (
    <div className="fixed inset-0 pointer-events-none z-[10000] overflow-hidden select-none">
      {/* Outer Magnetic Liquid Spotlight Ring with Smooth Fading Color Morph */}
      <motion.div
        style={{
          x: ringX,
          y: ringY,
          translateX: '-50%',
          translateY: '-50%',
        }}
        animate={{
          scale: isHovered ? 1.65 : 1,
          backgroundColor: isHovered
            ? `rgba(${activeColor.rgba}, 0.28)`
            : `rgba(${activeColor.rgba}, 0.12)`,
          borderColor: isHovered
            ? `rgba(${activeColor.rgba}, 1)`
            : `rgba(${activeColor.rgba}, 0.65)`,
        }}
        transition={{
          scale: { type: 'spring', stiffness: 500, damping: 30 },
          backgroundColor: { duration: 0.55, ease: 'easeInOut' },
          borderColor: { duration: 0.55, ease: 'easeInOut' },
        }}
        className="absolute w-9 h-9 rounded-full border-2 pointer-events-none"
      >
        {/* Glow is split into two statically-shadowed layers that crossfade via opacity
            instead of animating boxShadow directly — boxShadow is a paint-triggering property,
            and this element is simultaneously being transform-driven every frame by the
            mouse-following spring above, so animating it mid-tween meant repainting a moving
            layer. Opacity/transform-only crossfades are pure GPU composite work. */}
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ boxShadow: `0 0 12px rgba(${activeColor.rgba}, 0.35)` }}
          animate={{ opacity: isHovered ? 0 : 1 }}
          transition={{ duration: 0.55, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ boxShadow: `0 0 28px rgba(${activeColor.rgba}, 0.95), inset 0 0 12px rgba(${activeColor.rgba}, 0.4)` }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.55, ease: 'easeInOut' }}
        />
      </motion.div>

      {/* Inner Crisp Dot with Smooth Color Fading */}
      <motion.div
        style={{
          x: dotX,
          y: dotY,
          translateX: '-50%',
          translateY: '-50%',
        }}
        animate={{
          scale: isHovered ? 0.6 : 1,
          backgroundColor: isHovered ? '#ffffff' : activeColor.hex,
        }}
        transition={{
          scale: { type: 'spring', stiffness: 600, damping: 30 },
          backgroundColor: { duration: 0.55, ease: 'easeInOut' },
        }}
        className="absolute w-2.5 h-2.5 rounded-full pointer-events-none"
      >
        {/* Same static-shadow/opacity-crossfade technique as the outer ring */}
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ boxShadow: `0 0 10px ${activeColor.hex}` }}
          animate={{ opacity: isHovered ? 0 : 1 }}
          transition={{ duration: 0.55, ease: 'easeInOut' }}
        />
        <motion.div
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{ boxShadow: '0 0 14px #ffffff' }}
          animate={{ opacity: isHovered ? 1 : 0 }}
          transition={{ duration: 0.55, ease: 'easeInOut' }}
        />
      </motion.div>

      {/* Fading Click Shockwave Ripples */}
      {clickRipples.map((ripple) => (
        <motion.div
          key={ripple.id}
          initial={{ scale: 0.5, opacity: 0.9 }}
          animate={{ scale: 3.2, opacity: 0 }}
          transition={{ duration: 0.65, ease: 'easeOut' }}
          style={{
            left: ripple.x,
            top: ripple.y,
            translateX: '-50%',
            translateY: '-50%',
            borderColor: `rgba(${ripple.color}, 0.85)`,
          }}
          className="absolute w-12 h-12 rounded-full border-2 pointer-events-none"
        />
      ))}
    </div>
  )
}
